import type { FormalWorkflowGate } from '../../domain/inspectionContext';
import { JURISDICTION_CODE_ORDER, isJurisdictionCode } from '../../domain/jurisdiction';
import { MAX_SUPPORTED_DWELLING_UNITS, type V2Inspection } from '../../domain/inspection';
import { navigate } from '../../app/navigation';
import { useT } from '../../i18n/i18n';
import { setPropertyJurisdictionAction } from '../../state/inspectionStore';
import { Button, Card, Chip, Field, SectionHeader } from '../../ui/components';

// The FORMAL-WORKFLOW-CONTINUATION SEAM (NY F1 Round 1, Owner correction OC-1).
//
// This is the one place the product decides whether the formal New York inspection workflow may
// CONTINUE. It is rendered where the inspector enters the active field workspace, which every
// route into field work passes through — earlier than the field pass, and far earlier than report
// generation, which F1 forbids as the only PP-20 checkpoint.
//
// Design rules this card exists to satisfy:
//   - a legacy record is never blocked from being OPENED or VIEWED, and nothing on it is deleted,
//     reset or reinterpreted; only the formal workflow waits;
//   - the questions are answered HERE, in place, once — no wizard, no modal, no dead end. The
//     screen keeps its Back control, and an answer already given is never asked again;
//   - a wrong answer is correctable: the chips keep showing the recorded answer, including under
//     the hard block, so a mis-tap is not a trap;
//   - the copy states what the PRODUCT supports. It never makes a statement about the law, never
//     names a statute, and never shows an internal blocker or legal-review identifier.
export function FormalWorkflowGateCard(props: { inspection: V2Inspection; gate: FormalWorkflowGate }) {
  const { t } = useT();
  const { inspection, gate } = props;
  const occupancy = inspection.property.previouslyOccupied;

  function chooseState(value: string) {
    void setPropertyJurisdictionAction(inspection.id, { state: isJurisdictionCode(value) ? value : '' });
  }

  function chooseOccupancy(next: boolean) {
    void setPropertyJurisdictionAction(inspection.id, { previouslyOccupied: occupancy === next ? null : next });
  }

  const stateChooser = (
    <Field label={t('setup.state')} htmlFor="gate-state">
      <select
        id="gate-state"
        className="field-input"
        value={isJurisdictionCode(inspection.property.state) && inspection.property.stateConfirmed ? inspection.property.state : ''}
        onChange={(event) => chooseState(event.target.value)}
        data-testid="gate-state"
      >
        <option value="">{t('profile.selectState')}</option>
        {JURISDICTION_CODE_ORDER.map((code) => (
          <option key={code} value={code}>
            {code}
          </option>
        ))}
      </select>
    </Field>
  );

  const occupancyChooser = (
    <div data-testid="gate-occupancy">
      <div className="chips">
        <Chip label={t('common.yes')} selected={occupancy === true} onClick={() => chooseOccupancy(true)} testId="gate-occupancy-yes" />
        <Chip label={t('common.no')} selected={occupancy === false} onClick={() => chooseOccupancy(false)} testId="gate-occupancy-no" />
      </div>
    </div>
  );

  const profileLink = (
    <Button variant="secondary" icon="shield" onClick={() => navigate({ screen: 'settingsPage', page: 'profile' })} testId="gate-open-profile">
      {t('gate.openProfile')}
    </Button>
  );

  // NY F1 Round 5 (CK-10): the unit count is corrected here, in place, exactly like the occupancy
  // answer — a mis-tap is never a trap. "5 or more" records the limit + 1: what matters is that it
  // is above the supported range, and the exact number is not a fact the product invents.
  const unitChooser = (
    <div className="chips" data-testid="gate-units">
      {[1, 2, 3, 4, MAX_SUPPORTED_DWELLING_UNITS + 1].map((units) => (
        <Chip
          key={units}
          label={units > MAX_SUPPORTED_DWELLING_UNITS ? t('setup.unitCountOverLimit') : String(units)}
          selected={inspection.property.unitCount === units}
          onClick={() => void setPropertyJurisdictionAction(inspection.id, { unitCount: inspection.property.unitCount === units ? null : units })}
          testId={`gate-units-${units}`}
        />
      ))}
    </div>
  );

  function body() {
    switch (gate.kind) {
      case 'clear':
      case 'nonClientTest':
        return null;
      case 'inspectorStateMissing':
        return (
          <>
            <SectionHeader title={t('gate.licenceTitle')} />
            <p className="card-note">{t('gate.licenceBody')}</p>
            {profileLink}
          </>
        );
      case 'propertyStateUnconfirmed':
        return (
          <>
            <SectionHeader title={t('gate.stateTitle')} />
            <p className="card-note">{t('gate.stateBody')}</p>
            {stateChooser}
          </>
        );
      case 'unsupportedPropertyState':
        return (
          <>
            <SectionHeader title={t('gate.unsupportedTitle')} />
            <p className="card-note">{t('gate.unsupportedBody', { state: gate.state })}</p>
            {stateChooser}
          </>
        );
      case 'stateMismatch':
        return (
          <>
            <SectionHeader title={t('gate.mismatchTitle')} />
            <p className="card-note">{t('gate.mismatchBody', { inspectorState: gate.inspectorState, propertyState: gate.propertyState })}</p>
            {stateChooser}
            {profileLink}
          </>
        );
      case 'occupancyUnconfirmed':
        return (
          <>
            <SectionHeader title={t('gate.occupancyTitle')} />
            <p className="card-note">{t('gate.occupancyBody')}</p>
            {occupancyChooser}
          </>
        );
      case 'legalReviewBlock':
        return (
          <>
            <SectionHeader title={t('gate.unitCountTitle')} />
            <p className="card-note">{t('gate.unitCountBody')}</p>
            {unitChooser}
          </>
        );
      // X-1 (Owner default 2026-09-11): booked, eligible — waiting for the two signatures. The way
      // forward is the Agreement on the inspection details screen, one tap away.
      case 'agreementUnsigned':
        return (
          <>
            <SectionHeader title={t('gate.agreementTitle')} />
            <p className="card-note">{t('gate.agreementBody')}</p>
            <Button variant="secondary" icon="contract" onClick={() => navigate({ screen: 'inspectionDetails', inspectionId: inspection.id })} testId="gate-open-agreement">
              {t('gate.openAgreement')}
            </Button>
          </>
        );
    }
  }

  return (
    <div data-testid="formal-workflow-gate" data-gate={gate.kind}>
      <Card>
      <SectionHeader title={t('gate.title')} />
      <p className="card-note">{t('gate.viewOnly')}</p>
      {body()}
      </Card>
    </div>
  );
}
